import { useState, useEffect } from 'react';

interface TokenIconProps {
  symbol: string;
  fallbackIcon?: string;
  size?: number;
  className?: string;
}

// Web3icons raw GitHub URL for branded token icons
const WEB3ICONS_CDN = 'https://raw.githubusercontent.com/0xa3k5/web3icons/main/packages/core/src/svgs/tokens/branded';

// Token colors for fallback gradient
const TOKEN_COLORS: Record<string, string> = {
  'CC': '#8b5cf6',
  'ETH': '#627eea',
  'BTC': '#f7931a',
  'USDC': '#2775ca',
  'USDT': '#26a17b',
  'SOL': '#9945ff',
  'TRX': '#ff0013',
  'LINK': '#2a5ada',
  'AAVE': '#b6509e',
  'UNI': '#ff007a',
  'MATIC': '#8247e5',
  'ARB': '#28a0f0',
  'OP': '#ff0420',
  'AVAX': '#e84142',
  'BNB': '#f3ba2f',
  'DOT': '#e6007a',
  'ADA': '#0033ad',
  'XRP': '#23292f',
  'DOGE': '#c2a633',
  'SHIB': '#fda32b',
  'DAI': '#f5ac37',
  'WETH': '#627eea',
  'WBTC': '#f09242',
};

export function TokenIcon({ symbol, fallbackIcon, size = 24, className = '' }: TokenIconProps) {
  const [iconSrc, setIconSrc] = useState<'web3' | 'local' | 'fallback'>('web3');
  const upperSymbol = symbol.toUpperCase();
  const lowerSymbol = symbol.toLowerCase();

  // Reset icon source when symbol changes
  useEffect(() => {
    setIconSrc('web3');
  }, [symbol]);

  // Get token color for fallback
  const tokenColor = TOKEN_COLORS[upperSymbol] || '#8b5cf6';

  // Special case for Canton Coin - use local icon
  if (lowerSymbol === 'cc' || lowerSymbol === 'canton') {
    return (
      <img
        src="/tokens/canton.webp"
        alt="Canton Coin icon"
        width={size}
        height={size}
        className={`token-icon ${className}`}
        style={{ borderRadius: '50%' }}
        onError={() => setIconSrc('fallback')}
      />
    );
  }

  if (iconSrc === 'fallback') {
    return (
      <span
        className={`token-icon-fallback ${className}`}
        style={{
          width: size,
          height: size,
          fontSize: size * 0.45,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${tokenColor}, ${tokenColor}88)`,
          color: 'white',
          fontWeight: 600,
        }}
      >
        {fallbackIcon || upperSymbol.substring(0, 2)}
      </span>
    );
  }

  if (iconSrc === 'local') {
    return (
      <img
        src={`/tokens/${lowerSymbol}.webp`}
        alt={`${symbol} icon`}
        width={size}
        height={size}
        className={`token-icon ${className}`}
        onError={() => setIconSrc('fallback')}
        style={{ borderRadius: '50%' }}
      />
    );
  }

  // Default: try web3icons CDN first
  return (
    <img
      src={`${WEB3ICONS_CDN}/${upperSymbol}.svg`}
      alt={`${symbol} icon`}
      width={size}
      height={size}
      className={`token-icon ${className}`}
      onError={() => setIconSrc('local')}
      style={{ borderRadius: '50%' }}
    />
  );
}

export default TokenIcon;
